import {
    Component,
    ElementRef,
    EventEmitter,
    inject,
    Input,
    OnInit,
    ViewChild,
} from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { WdxButtonStyle } from '@wdx/shared/components/wdx-button';
import {
    LogicStepDefinition,
    Severity,
    TranslationsService,
    WdxDestroyClass,
    WorkflowConfiguration,
} from '@wdx/shared/utils';
import { NodeAddedEvent, ParamsChangedEvent, WorkflowNode } from '../../models';
import { WorkflowDesignerFacade } from '../../services';
import { IWrappedWorkflowFormComponentProvider } from '../../interfaces';
import { WdxToastService } from '@wdx/shared/components/wdx-toast';
import { WorkflowDesignerConfig } from '../../models/workflow-designer-config';
import {
    WdxDialogComponent,
    WdxDialogService,
} from '@wdx/shared/components/wdx-dialog';
import { takeUntil } from 'rxjs';

@Component({
    selector: 'clmi-workflow-designer',
    templateUrl: './workflow-designer.component.html',
    standalone: false,
})
export class WorkflowDesignerComponent
    extends WdxDestroyClass
    implements OnInit
{
    @ViewChild('leftPanel') leftPanel!: ElementRef<HTMLDivElement>;

    private facade = inject(WorkflowDesignerFacade);
    private translationsService = inject(TranslationsService);
    private toastService = inject(WdxToastService);
    private wdxDialogService = inject(WdxDialogService);
    public activeModal = inject(NgbActiveModal);

    private _config: WorkflowDesignerConfig;
    private nodeAdded: NodeAddedEvent;
    private isDirty = false;

    @Input() set config(value: WorkflowDesignerConfig) {
        if (value) {
            this._config = value;
            this.facade.setConfig(value);
        }
    }
    get config() {
        return this._config;
    }
    @Input() workflowOutput = new EventEmitter<WorkflowConfiguration>();
    @Input() componentProvider?: IWrappedWorkflowFormComponentProvider;

    workflowNodes$ = this.facade.workflowNodes$;
    workflowContext$ = this.facade.workflowContext$;
    groupedSteps$ = this.facade.groupedStepDefinitions$;
    showingStepSelection$ = this.facade.showingStepSelection$;

    DESIGNER_TITLE = 'WORKFLOW_DESIGNER_TITLE';
    WdxButtonStyle = WdxButtonStyle;

    activeNodeId: string;

    ngOnInit(): void {
        this.facade.loadDefinitions();
    }

    onBeforeDismiss(): boolean | Promise<boolean> {
        if (!this.isDirty) {
            this.facade.clear();
            this.nodeAdded = null;
            this.toastService.clear();
            return true;
        }
        const confirmDialog = this.wdxDialogService.open(WdxDialogComponent);
        confirmDialog.componentInstance.title =
            this.translationsService.translateTokenisedString(
                'WORKFLOW_DESIGNER_CONFIRM_UNSAVED_TITLE',
            );
        confirmDialog.componentInstance.dialogBody =
            this.translationsService.translateTokenisedString(
                'WORKFLOW_DESIGNER_CONFIRM_UNSAVED_DESCRIPTION',
            );

        confirmDialog.componentInstance.wdxOnClick
            .pipe(takeUntil(this.destroyed$))
            .subscribe(() => {
                this.facade.clear();
                this.nodeAdded = null;
                this.isDirty = false;
                this.toastService.clear();
            });

        return confirmDialog.result;
    }

    onNodeSelected(node: WorkflowNode | null): void {
        this.nodeAdded = null;
        this.facade.clear();
        if (node) {
            this.facade.selectNode(node);
        }
        this.scrollLeftPanelToTop();
    }

    onNodeAdd(event: NodeAddedEvent): void {
        this.nodeAdded = event;
        this.facade.startStepSelection(event);
        this.scrollLeftPanelToTop();
    }

    onNodeRemove(node: WorkflowNode): void {
        this.facade.removeNode(node);
        this.isDirty = true;
    }

    onStepClicked(stepDef: LogicStepDefinition): void {
        this.activeNodeId = this.facade.startAddNode(stepDef, this.nodeAdded);
        this.scrollLeftPanelToTop();
        this.isDirty = true;
    }

    onParamsChanged(event: ParamsChangedEvent) {
        this.facade.updateNode(event.formValues, event.isValid);
        this.isDirty = true;
    }

    saveWorkflow() {
        const isWorkflowValid = this.facade.allNodesValid();
        const toastOptions = {
            translation: isWorkflowValid
                ? 'WORKFLOW_SAVE_SUCCESS_VALID'
                : 'WORKFLOW_SAVE_SUCCESS_INVALID',
            severity: isWorkflowValid ? Severity.Success : Severity.Warning,
        };

        this.facade.saveWorkflow().subscribe({
            next: (output) => {
                this.workflowOutput.emit(output);
                this.isDirty = false;
            },
            error: (err) => {
                toastOptions.severity = Severity.Danger;
                toastOptions.translation = 'WORKFLOW_SAVE_ERROR';
            },
            complete: () => {
                this.toastService.show({
                    closeOthers: true,
                    body: [
                        this.translationsService.translateTokenisedString(
                            toastOptions.translation,
                        ),
                    ],
                    severity: toastOptions.severity,
                });
            },
        });
    }

    private scrollLeftPanelToTop() {
        setTimeout(() => {
            if (this.leftPanel) {
                this.leftPanel.nativeElement.scrollTop = 0;
            }
        });
    }
}
